import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Fade,
  Button,
} from '@mui/material';
import {
  CheckCircle,
  Visibility,
  ListAlt,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { useChatContext } from '../../agent/contexts/ChatContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { flowiseService } from '../../agent/services/FlowiseService';

export interface AnimalAddedData {
  resumo?: string;
  url?: string;
}

export interface AnimalAddedSuccessViewProps {
  animalAddedData: AnimalAddedData;
}

const AnimalAddedSuccessView: React.FC<AnimalAddedSuccessViewProps> = ({
  animalAddedData,
}) => {
  const { t } = useTranslation('animal');
  const { resumo } = animalAddedData;
  const { 
    sendMessage, 
    activeSessionId,
    createSession,
    setActiveSession,
    organizations,
    selectedOrganization
  } = useChatContext();
  const { authState } = useAuthContext();
  const [selectedFlow, setSelectedFlow] = useState<string>('');

  // Extract animal name and lot name from resumo
  const extractInfo = (resumoText: string) => {
  
    
    // Padrão mais amplo: captura qualquer coisa com "ao lote" e ":"
    // Exemplo: "1 animal adicionados ao lote 'Recria10': Mimosa"
    let match = resumoText.match(/ao lote '([^']+)':\s*(.+)$/);
    if (match) {
  
      return {
        lotName: match[1],
        animalName: match[2].trim()
      };
    }
    

    return { lotName: 'Lote', animalName: 'Animal' };
  };

  const { lotName, animalName } = extractInfo(resumo || '');

  // Resolve o ID da organização para criar uma sessão
  const resolveOrganizationId = (): string | undefined => {
    const org = selectedOrganization || (organizations && organizations[0]);
    // Alguns objetos Farm usam _key; outros usam id
    return (org as any)?._key || org?.id;
  };

  // Carrega o fluxo padrão (igual aos outros feedbacks)
  useEffect(() => {
    const loadDefaultFlow = async () => {
      const userId = authState.profileId;
      if (!userId) return;
      try {
        const flows = await flowiseService.getAvailableFlows(userId);
        setSelectedFlow(flows[0]?.id || '');
      } catch (err) {
        console.error('Erro ao carregar flows:', err);
      }
    };
    loadDefaultFlow();
  }, [authState.profileId]);

  // Envia mensagem de sugestão para o chat, criando sessão se necessário
  const handleSendMessage = async (message: string) => {
    try {
      // Se não há sessão ativa, cria uma nova na organização atual
      if (!activeSessionId) {
        const orgId = resolveOrganizationId();
        if (!orgId) {
          console.error('Nenhuma organização disponível para iniciar a conversa');
          return;
        }
        const newSessionId = await createSession(orgId);
        await setActiveSession(newSessionId);
      }

      // Garantir selectedFlow; se não tiver, tenta carregar on-demand
      let flowId = selectedFlow;
      if (!flowId) {
        const userId = authState.profileId;
        if (!userId) {
          console.error('Sem userId para carregar flows');
        } else {
          try {
            const flows = await flowiseService.getAvailableFlows(userId);
            flowId = flows[0]?.id || '';
            setSelectedFlow(flowId);
          } catch (err) {
            console.error('Erro ao carregar flows on-demand:', err);
          }
        }
      }

      await sendMessage(message, flowId);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    }
  };

  const suggestedActions = [
    {
      label: `mostre o lote ${lotName}`,
      message: `mostre o lote ${lotName}`,
      icon: <Visibility />,
      color: DESIGN_TOKENS.colors.brand.primary,
    },
    {
      label: 'Liste todos os meus lotes',
      message: 'Liste todos os meus lotes',
      icon: <ListAlt />,
      color: DESIGN_TOKENS.colors.primary[700],
    },
  ];

  return (
    <Box sx={{ 
      p: 4, 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      minHeight: '400px',
      textAlign: 'center'
    }}>
      <Fade in timeout={600}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Success Icon */}
          <Avatar
            sx={{
              width: 120,
              height: 120,
              bgcolor: DESIGN_TOKENS.colors.brand.primary,
              mb: 3,
              boxShadow: `0 8px 32px ${DESIGN_TOKENS.colors.brand.surface}`,
            }}
          >
            <CheckCircle sx={{ fontSize: '4rem', color: 'white' }} />
          </Avatar>

          {/* Success Message */}
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              color: DESIGN_TOKENS.colors.brand.primary,
              mb: 2
            }}
          >
            {t('success.animal_added_title')}
          </Typography>

          <Typography 
            variant="h6" 
            sx={{ 
              color: DESIGN_TOKENS.colors.text.primary,
              mb: 1,
              fontWeight: 600
            }}
          >
            {resumo || t('success.animal_added_default_message')}
          </Typography>

          {/* Animal Details Card */}
          <Card sx={{ 
            mt: 3, 
            mb: 4, 
            minWidth: 300,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: `2px solid ${DESIGN_TOKENS.colors.brand.surface}`
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: '2rem',
                    height: '2rem',
                    backgroundColor: DESIGN_TOKENS.colors.organization.farm,
                    mask: 'url(/vaca.png) no-repeat center',
                    maskSize: 'contain',
                    WebkitMask: 'url(/vaca.png) no-repeat center',
                    WebkitMaskSize: 'contain',
                  }}
                />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.organization.farm }}>
                    {t('success.animal_label', { animalName })}
                  </Typography>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    {t('success.added_to_lot', { lotName })}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    {t('success.lot_label')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {lotName}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    {t('success.animal_field_label')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {animalName}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Additional Info */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: DESIGN_TOKENS.colors.text.secondary,
              mt: 3,
              fontStyle: 'italic'
            }}
          >
            {t('success.management_info')}
          </Typography>

          {/* Ações sugeridas */}
          <Box sx={{ mt: 3, width: '100%', maxWidth: 480 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: DESIGN_TOKENS.colors.text.secondary,
                fontWeight: 600,
                mb: 1,
              }}
            >
              Ações sugeridas:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {suggestedActions.map((action, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  startIcon={action.icon}
                  onClick={() => handleSendMessage(action.message)}
                  sx={{
                    borderColor: action.color,
                    color: action.color,
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    padding: '8px 16px',
                    '&:hover': {
                      backgroundColor: `${action.color}10`,
                      borderColor: action.color,
                    },
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};

export default AnimalAddedSuccessView;